import { gql } from "@apollo/client";
import { useMutation } from "@apollo/client/react";
import {
  DSButton,
  DSCardWrapper,
} from "@components/design-system/molecules";
import {
  faCheckCircle,
  faClock,
  faQuestionCircle,
  faXmark,
  faTimesCircle,
  faSort,
} from "@fortawesome/pro-solid-svg-icons";
import { FontAwesomeIcon } from "@fortawesome/react-fontawesome";
import { Tooltip } from "@mui/material";
import moment from "moment";
import { useState } from "react";

import { alert } from "../../common/Alert";

const REVOKE_INVITATION = gql`
  mutation adminRevokeInvitation(
    $teamUuid: String!
    $inviteeEmail: String!
  ) {
    adminRevokeInvitation(
      teamUuid: $teamUuid
      inviteeEmail: $inviteeEmail
    ) {
      ok
    }
  }
`;

const RESEND_INVITATION = gql`
  mutation adminResendInvitation(
    $teamUuid: String!
    $inviteeEmail: String!
    $createdAt: DateTime!
  ) {
    adminResendInvitation(
      teamUuid: $teamUuid
      inviteeEmail: $inviteeEmail
      createdAt: $createdAt
    ) {
      ok
    }
  }
`;

export function EditInvitedTeamMembers({ teamUuid, team, refetch }) {
  const [sortColumn, setSortColumn] = useState(null);
  const [isSortReverse, setIsSortReverse] = useState(false);

  const sortInvites = (sortBy) => {
    const newReverse = sortColumn === sortBy ? !isSortReverse : false;
    setIsSortReverse(newReverse);
    setSortColumn(sortBy);
  };

  const allInvites = team?.teamInvites || [];
  const sortedInvites = sortColumn
    ? [...allInvites].sort((a, b) => {
        const aValue = a[sortColumn] ?? "";
        const bValue = b[sortColumn] ?? "";

        if (typeof aValue === "number") {
          return isSortReverse ? bValue - aValue : aValue - bValue;
        } else if (moment(aValue, moment.ISO_8601, true).isValid()) {
          return isSortReverse
            ? moment(bValue).diff(moment(aValue))
            : moment(aValue).diff(moment(bValue));
        } else {
          return isSortReverse
            ? String(bValue).localeCompare(String(aValue))
            : String(aValue).localeCompare(String(bValue));
        }
      })
    : allInvites;

  function renderIcon(inviteeStatus) {
    switch (inviteeStatus) {
      case "accepted":
        return (
          <FontAwesomeIcon
            icon={faCheckCircle}
            className="text-success"
          />
        );
      case "pending":
        return (
          <FontAwesomeIcon icon={faClock} className="text-warning" />
        );
      case "revoked":
        return (
          <FontAwesomeIcon
            icon={faTimesCircle}
            className="text-danger"
          />
        );
      case "rejected":
        return (
          <FontAwesomeIcon icon={faXmark} className="text-danger" />
        );
      default:
        return (
          <Tooltip
            arrow
            placement="top"
            title="This invite has expired without being responded to."
          >
            <FontAwesomeIcon
              icon={faQuestionCircle}
              className="text-secondary"
            />
          </Tooltip>
        );
    }
  }

  return (
    <div className="my-md">
      <DSCardWrapper
        variant="default"
        padding="py-sm px-sm"
        dataTestId="invited-team-members-table"
      >
        <table className="table-auto w-full text-primary text-left">
          <thead>
            <tr className="border-b border-primary">
              <SortableHeader
                title="Invitee Email"
                sortBy="inviteeEmail"
                sortInvites={sortInvites}
              />
              <SortableHeader
                title="Invite Status"
                sortBy="status"
                sortInvites={sortInvites}
              />
              <SortableHeader
                title="Time Sent"
                sortBy="timeSent"
                sortInvites={sortInvites}
              />
              <SortableHeader
                title="Time Responded"
                sortBy="timeResponded"
                sortInvites={sortInvites}
              />
              <th></th>
              <th></th>
            </tr>
          </thead>
          <tbody>
            {sortedInvites.map((invite) => (
              <tr
                key={invite.uuid}
                className="h-10 even:bg-transparent odd:bg-primary rounded-standard"
              >
                <td>{invite.inviteeEmail}</td>
                <td>
                  {renderIcon(invite.status)}{" "}
                  {invite.status.charAt(0).toUpperCase() +
                    invite.status.slice(1)}
                </td>
                <td>
                  {moment(invite.timeSent).format("MMM. DD, h:mm a")}
                </td>
                <td>
                  {invite.timeResponded
                    ? moment(invite.timeResponded).format(
                        "MMM. DD, h:mm a",
                      )
                    : "-"}
                </td>
                <td className="flex py-2 gap-sm">
                  <ResendInviteButton
                    invite={invite}
                    teamUuid={teamUuid}
                    refetch={refetch}
                  />
                  <RevokeInviteButton
                    invite={invite}
                    teamUuid={teamUuid}
                    refetch={refetch}
                  />
                </td>
              </tr>
            ))}
          </tbody>
        </table>
      </DSCardWrapper>
    </div>
  );
}

function SortableHeader({ title, sortBy, sortInvites }) {
  return (
    <th
      className="font-semibold text-sm cursor-pointer"
      onClick={() => sortInvites(sortBy)}
    >
      {title}{" "}
      <FontAwesomeIcon icon={faSort} size="xs" className="ml-1" />
    </th>
  );
}

function ResendInviteButton({ invite, refetch, teamUuid }) {
  const [resendInvitation] = useMutation(RESEND_INVITATION);
  const isRevoked = invite?.status === "revoked";
  const isAccepted = invite?.status === "accepted";

  if (isRevoked || isAccepted) return <div className="h-10"></div>;

  return (
    <DSButton
      variant="secondary"
      size="sm"
      onClick={() => {
        resendInvitation({
          variables: {
            teamUuid,
            inviteeEmail: invite?.inviteeEmail,
            createdAt: new Date(),
          },
        })
          .then(() => {
            alert("success", "Invite sent");
            refetch();
          })
          .catch((err) => {
            console.log(err);
            refetch();
          });
      }}
      dataTestId="resend-invite-button"
    >
      Resend Invite
    </DSButton>
  );
}

function RevokeInviteButton({ invite, teamUuid, refetch }) {
  const [revokeInvitation] = useMutation(REVOKE_INVITATION);
  const isRevoked = invite?.status === "revoked";
  const isAccepted = invite?.status === "accepted";
  const isExpired = invite?.status === "expired";

  if (isRevoked || isAccepted || isExpired)
    return <div className="h-10"></div>;

  return (
    <DSButton
      variant="secondary"
      size="sm"
      onClick={() => {
        revokeInvitation({
          variables: {
            teamUuid,
            inviteeEmail: invite?.inviteeEmail,
          },
        })
          .then(() => {
            alert("success", "Invite revoked.");
            refetch();
          })
          .catch((err) => {
            console.log(err);
            refetch();
          });
      }}
      dataTestId="revoke-invite-button"
    >
      Revoke Invite
    </DSButton>
  );
}
